/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import {
    DurationColumn,
    GridColumnJustify,
    GridSortDirections,
    PageDialog,
    ConfiguredGrid,
    useGlobalAppData,
    useGlobalLang,
    useGlobalServices,
} from '@veeam-vspc/shared/components';
import { DurationFormatTypes } from '@veeam-vspc/shared/helpers';

import type { RequestSuccessResponse } from '@veeam-vspc/shared/interfaces';
import type { PageDialogProps } from '@veeam-vspc/shared/components';
import type { JobProcessedVmParam, BackupJobProcessedVmDataModel } from '@veeam-vspc/models/web-controllers';
import type { VspcLang } from 'configs/languages';

import { JobTypes } from 'core/enums';
import { GridDateColumn } from 'views/components/columns/GridDateColumn';
import { RestorePointSizeColumn } from 'views/components/columns/RestorePointSizeColumn';
import { StatusColumn } from 'views/components/columns/StatusColumn';
import { RestorePointColumn } from 'views/pages/BackupJobsPage/components/RestorePointColumn';
import { ProcessedVmsDialogToolbar } from '../ProcessedVmsDialogToolbar';
import { getFailureMessage } from 'views/components/FailureMessageDialog';
import {
    RestorePointColumnsFields,
} from 'views/pages/BackupJobsPage/components/RestorePointColumn/components/RestorePointDialog';
import {
    JobSessionTaskStatusRepresentation,
    JobSessionTaskStatusRepresentationText,
} from '../../../../../../../../enums';


export interface ProcessedVmsJobsDialogProps extends Omit<PageDialogProps, 'content'> {
    jobUid: string;
    stateId: string;
    jobType: JobTypes;
}

export const ProcessedVmsJobsDialog: React.FC<ProcessedVmsJobsDialogProps> = (props) => {
    const lang = useGlobalLang<VspcLang>();
    const { formats } = useGlobalAppData();
    const { netShortDate, netShortTime } = formats;
    const { transportService } = useGlobalServices();
    const { jobUid, stateId, title, description, disabledBack, hasBack, footer, onClose, jobType } = props;
    const gridStateId = `${stateId}.Dialogs.ProcessedVms`;

    return (
        <PageDialog
            title={title}
            description={description}
            disabledBack={disabledBack}
            hasBack={hasBack}
            footer={footer}
            onClose={onClose}
            content={(
                <ConfiguredGrid
                    stateId={gridStateId}
                    initialState={{
                        filters: {
                            jobUid,
                        },
                        sort: {
                            direction: GridSortDirections.Asc,
                            key: 'name',
                        },
                    }}
                    defaultColumnMinWidth={100}
                    columns={[
                        {
                            id: 'name',
                            title: lang.OBJECT,
                            cell: rowData => rowData.name,
                        },
                        {
                            id: 'status',
                            title: lang.STATUS,
                            cell: (rowData) => {
                                const statusName = rowData.status;
                                const linkStatuses = [
                                    JobSessionTaskStatusRepresentation.Failed,
                                    JobSessionTaskStatusRepresentation.Warning,
                                ];
                                const isLink = !!rowData.failureMessage && linkStatuses.includes(statusName);

                                return (
                                    <StatusColumn
                                        status={
                                            JobSessionTaskStatusRepresentationText[rowData.status]
                                        }
                                        messageBoxTitle={lang.STATUS}
                                        message={isLink &&
                                            getFailureMessage(lang, {
                                                statusName,
                                                shortDateFormat: netShortDate,
                                                shortTimeFormat: netShortTime,
                                                failureMessage: rowData.failureMessage,
                                                lastSessionTime: rowData.lastSessionEndTime,
                                            })}
                                    />
                                );
                            },
                        },
                        {
                            id: 'transferredDataKb',
                            title: lang.TRANSFERRED_DATA,
                            cell: rowData =>
                                <RestorePointSizeColumn value={rowData.transferredDataKb} />,
                            headerJustify: GridColumnJustify.Right,
                            justify: GridColumnJustify.Right,
                        },
                        {
                            id: 'guestOS',
                            title: lang.GUEST_OS,
                            cell: rowData => rowData.guestOS,
                        },
                        {
                            id: 'restorePointsCount',
                            title: lang.RESTORE_POINTS,
                            cell: (rowData) => {
                                const isReplicaJob = jobType === JobTypes.Replica || jobType === JobTypes.VcdReplica;
                                const hiddenColumns = isReplicaJob ? [
                                    RestorePointColumnsFields.SourceSize,
                                    RestorePointColumnsFields.BackedUpData,
                                    RestorePointColumnsFields.RestorePointSize,
                                ] : [];

                                return (
                                    <RestorePointColumn
                                        value={rowData.restorePointsCount}
                                        stateId={`${gridStateId}.Dialogs.RestorePoints`}
                                        name={rowData.name}
                                        noteBarIsHidden={rowData.isOneBackupPerVmEnabled || isReplicaJob}
                                        hiddenColumns={hiddenColumns}
                                        extraParams={{
                                            jobUid: jobUid,
                                            vmUid: rowData.vmUid,
                                        }}
                                    />
                                );
                            },
                            headerJustify: GridColumnJustify.Right,
                            justify: GridColumnJustify.Right,
                        },
                        {
                            id: 'lastSuccessSession',
                            title: lang.LAST_RUN,
                            cell: rowData => <GridDateColumn value={rowData.lastSuccessSession} />,
                        },
                        {
                            id: 'lastDurationSessionSec',
                            title: lang.LAST_DURATION,
                            cell: rowData => (
                                <DurationColumn
                                    value={rowData.lastDurationSessionSec}
                                    formatType={DurationFormatTypes.TIMEw8}
                                />
                            ),
                            headerJustify: GridColumnJustify.Right,
                            justify: GridColumnJustify.Right,
                        },
                    ]}
                    data={(requestParams: JobProcessedVmParam) => transportService
                        .request<typeof requestParams, RequestSuccessResponse<BackupJobProcessedVmDataModel[]>>(
                            '/BackupServerJob/GetProcessedVms',
                            { jobUid, ...requestParams },
                        )}
                    toggleable={false}
                    selection={{
                        field: 'vmUid',
                        checkbox: true,
                        multiple: true,
                    }}
                    toolbars={[
                        () => <ProcessedVmsDialogToolbar idProperty={'vmUid'} />,
                    ]}
                />
            )}
        />
    );
};
